/**
 * Social Sharing Component
 * Share safety scores, incidents, and reports on social media
 */
import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Share2,
  Twitter,
  Facebook,
  Link,
  Mail,
  MessageCircle,
  CheckCircle,
  Copy
} from 'lucide-react';

// Share types
const SHARE_TYPES = {
  SAFETY_SCORE: 'safety_score',
  ROUTE_SAFETY: 'route_safety',
  INCIDENT: 'incident',
  GENERAL: 'general'
};

// Generate share text based on type
const generateShareText = (type, data) => {
  const appUrl = window.location.origin;
  
  switch (type) {
    case SHARE_TYPES.SAFETY_SCORE:
      return {
        title: `Safety Score: ${data.grade} (${data.score}/100)`,
        text: `I checked the safety score for ${data.address || 'my location'} on VeriWatch - it's a ${data.grade}! 🛡️`,
        url: `${appUrl}/safety?tab=property`,
        hashtags: ['VeriWatch', 'SafetyFirst', 'PublicSafety']
      };
    
    case SHARE_TYPES.ROUTE_SAFETY:
      return {
        title: `Route Safety: Grade ${data.grade}`,
        text: `Planning a trip? I analyzed my route on VeriWatch - it scored ${data.score}/100 for safety! 🚗`,
        url: `${appUrl}/safety?tab=route`,
        hashtags: ['VeriWatch', 'TravelSafe', 'RoutePlanning']
      };
    
    case SHARE_TYPES.INCIDENT:
      return {
        title: `Safety Alert: ${data.title}`,
        text: `⚠️ ${data.type} reported near ${data.location || 'my area'}. Stay safe! #VeriWatch`,
        url: `${appUrl}/safety?tab=timeline`,
        hashtags: ['VeriWatch', 'SafetyAlert', 'StaySafe']
      };
    
    default:
      return {
        title: 'VeriWatch - Public Safety Intelligence',
        text: 'Stay informed about safety in your area with VeriWatch - live scanner feeds, safety scores, and real-time alerts! 🛡️',
        url: appUrl,
        hashtags: ['VeriWatch', 'PublicSafety']
      };
  }
};

// Share to specific platforms
const shareToTwitter = ({ text, url, hashtags }) => {
  const hashtagsStr = hashtags?.join(',') || '';
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}&hashtags=${hashtagsStr}`;
  window.open(twitterUrl, '_blank', 'width=600,height=400');
};

const shareToFacebook = ({ url }) => {
  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
  window.open(fbUrl, '_blank', 'width=600,height=400');
};

const shareToWhatsApp = ({ text, url }) => {
  const waUrl = `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`;
  window.open(waUrl, '_blank');
};

const shareByEmail = ({ title, text, url }) => {
  const subject = encodeURIComponent(title);
  const body = encodeURIComponent(`${text}\n\n${url}`);
  window.location.href = `mailto:?subject=${subject}&body=${body}`;
};

// Native share API
const nativeShare = async ({ title, text, url }) => {
  if (navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return true;
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Share failed:', err);
      }
    }
  }
  return false;
};

// Main Share Button Component
export const ShareButton = ({ 
  type = SHARE_TYPES.GENERAL, 
  data = {},
  variant = 'ghost',
  size = 'sm',
  showLabel = false 
}) => {
  const [copied, setCopied] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  
  const shareData = generateShareText(type, data);

  const handleShare = useCallback(async () => {
    // Try native share first
    const shared = await nativeShare(shareData);
    if (!shared) {
      setShowDialog(true);
    }
  }, [shareData]);

  const copyLink = useCallback(() => {
    navigator.clipboard.writeText(shareData.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [shareData.url]);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant={variant} size={size} className="gap-2">
            <Share2 className="w-4 h-4" />
            {showLabel && <span>Share</span>}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="glass-card border-slate-800 w-48">
          <DropdownMenuItem onClick={() => shareToTwitter(shareData)} className="cursor-pointer">
            <Twitter className="w-4 h-4 mr-2 text-blue-400" />
            Twitter / X
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => shareToFacebook(shareData)} className="cursor-pointer">
            <Facebook className="w-4 h-4 mr-2 text-blue-600" />
            Facebook
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => shareToWhatsApp(shareData)} className="cursor-pointer">
            <MessageCircle className="w-4 h-4 mr-2 text-green-500" />
            WhatsApp
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => shareByEmail(shareData)} className="cursor-pointer">
            <Mail className="w-4 h-4 mr-2 text-amber-400" />
            Email
          </DropdownMenuItem>
          <DropdownMenuItem onClick={copyLink} className="cursor-pointer">
            {copied ? (
              <>
                <CheckCircle className="w-4 h-4 mr-2 text-green-400" />
                Copied!
              </>
            ) : (
              <>
                <Link className="w-4 h-4 mr-2" />
                Copy Link
              </>
            )}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Share Dialog for non-native share */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="glass-card border-slate-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Share2 className="w-5 h-5 text-teal-400" />
              Share VeriWatch
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-4">
            <Button
              variant="outline"
              onClick={() => shareToTwitter(shareData)}
              className="flex flex-col items-center gap-2 h-auto py-4"
            >
              <Twitter className="w-6 h-6 text-blue-400" />
              <span className="text-xs">Twitter</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => shareToFacebook(shareData)}
              className="flex flex-col items-center gap-2 h-auto py-4"
            >
              <Facebook className="w-6 h-6 text-blue-600" />
              <span className="text-xs">Facebook</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => shareToWhatsApp(shareData)}
              className="flex flex-col items-center gap-2 h-auto py-4"
            >
              <MessageCircle className="w-6 h-6 text-green-500" />
              <span className="text-xs">WhatsApp</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => shareByEmail(shareData)}
              className="flex flex-col items-center gap-2 h-auto py-4"
            >
              <Mail className="w-6 h-6 text-amber-400" />
              <span className="text-xs">Email</span>
            </Button>
          </div>
          <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-800/50 border border-slate-700">
            <input
              type="text"
              value={shareData.url}
              readOnly
              className="flex-1 bg-transparent text-sm text-slate-300 outline-none"
            />
            <Button size="sm" variant="ghost" onClick={copyLink}>
              {copied ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Export share types for external use
export { SHARE_TYPES };

export default ShareButton;
